import { useEffect, useMemo, useState } from "react";
import {
  Trophy, ShoppingBag, Package, BarChart3, Gift, Plus, Trash2, Edit3, Save,
  X, CheckCircle2, XCircle, Truck, Loader2, Gem, Zap, Users, Award, Star,
  Filter, Coins, AlertTriangle, Sliders, RotateCcw, Flame, Calendar,
  ToggleLeft, ToggleRight, PlayCircle,
} from "lucide-react";
import { toast } from "sonner";
import { GamificationAdmin } from "../lib/api";
import { useConfirm } from "../components/ConfirmProvider";
import { PageSkeleton } from "../components/PageSkeleton";

const TABS = [
  { id: "config",       label: "Konfiguracja XP", icon: Sliders },
  { id: "boosts",       label: "XP Boost",        icon: Flame },
  { id: "achievements", label: "Osiągnięcia", icon: Trophy },
  { id: "shop", label: "Sklep", icon: ShoppingBag },
  { id: "orders", label: "Zamówienia", icon: Package },
  { id: "grants", label: "Ręczne przyznawanie", icon: Gift },
  { id: "stats", label: "Statystyki", icon: BarChart3 },
];

const CONDITION_TYPES = [
  { value: "packages_total",       label: "Łączna liczba paczek ≥" },
  { value: "packages_week",        label: "Paczki w ostatnie 7 dni ≥" },
  { value: "cod_total",            label: "Suma pobrań (zł) ≥" },
  { value: "routes_completed",     label: "Trasy ukończone w 100% ≥" },
  { value: "streak_days",          label: "Passa dni z rzędu ≥" },
  { value: "night_delivery",       label: "Nocnych dostarczeń ≥" },
  { value: "effectiveness_perfect",label: "Skuteczność 100% (min. paczek)" },
  { value: "manual",               label: "Ręcznie (przyznaje admin)" },
];

const SHOP_CATEGORIES = [
  { value: "gadget",     label: "Gadżet" },
  { value: "perk",       label: "Perk / dodatek" },
  { value: "multiplier", label: "Mnożnik" },
  { value: "voucher",    label: "Voucher" },
];

const ORDER_STATUSES = [
  { value: "",          label: "Wszystkie" },
  { value: "pending",   label: "Oczekujące" },
  { value: "approved",  label: "Zaakceptowane" },
  { value: "delivered", label: "Wydane" },
  { value: "rejected",  label: "Odrzucone" },
];

export default function GamificationAdminPage() {
  const [tab, setTab] = useState("config");
  return (
    <div className="p-3 md:p-6 space-y-5" data-testid="gamification-admin-page">
      <div className="relative overflow-hidden rounded-3xl bg-gradient-to-br from-zinc-900 via-zinc-950 to-black text-white p-6 md:p-8 ring-1 ring-white/10 shadow-[0_25px_60px_-25px_rgba(0,0,0,0.7)]">
        <Trophy className="absolute -right-10 -bottom-16 w-72 h-72 opacity-[0.10]" strokeWidth={1.5} />
        <div className="relative flex items-center gap-2">
          <div className="w-10 h-10 rounded-lg bg-white/15 flex items-center justify-center ring-1 ring-white/25">
            <Trophy className="w-5 h-5" />
          </div>
          <div className="text-[10px] font-mono uppercase tracking-widest text-white/70">
            // Panel administratora · Gamifikacja
          </div>
        </div>
        <h1 className="relative mt-3 font-heading font-black text-2xl md:text-4xl uppercase tracking-tight">
          System osiągnięć & Sklep GoPossible
        </h1>
        <div className="relative mt-5 flex gap-2 flex-wrap">
          {TABS.map((t) => (
            <button
              key={t.id}
              data-testid={`gami-admin-tab-${t.id}`}
              onClick={() => setTab(t.id)}
              className={`inline-flex items-center gap-2 px-3 py-2 rounded-xl text-xs font-bold uppercase tracking-widest transition-colors ${
                tab === t.id ? "bg-white text-zinc-900" : "bg-white/10 text-white/80 hover:bg-white/20"
              }`}
            >
              <t.icon className="w-4 h-4" />
              {t.label}
            </button>
          ))}
        </div>
      </div>

      {tab === "config" && <ConfigTab />}
      {tab === "boosts" && <BoostsTab />}
      {tab === "achievements" && <AchievementsTab />}
      {tab === "shop" && <ShopTab />}
      {tab === "orders" && <OrdersTab />}
      {tab === "grants" && <GrantsTab />}
      {tab === "stats" && <StatsTab />}
    </div>
  );
}

// =============================================================================
// Config Tab — XP formula & level ladder tuning
// =============================================================================
function ConfigTab() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);
  const { confirm } = useConfirm();

  const load = () => {
    setLoading(true);
    GamificationAdmin.getConfig()
      .then((d) => { setData(d); setForm(d.config); })
      .catch(() => toast.error("Nie mogę pobrać konfiguracji"))
      .finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, []);

  const save = async () => {
    setSaving(true);
    try {
      await GamificationAdmin.updateConfig(form);
      toast.success("Konfiguracja zapisana. Zmiany wchodzą natychmiast.");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Błąd zapisu");
    } finally {
      setSaving(false);
    }
  };

  const reset = async () => {
    if (!(await confirm("Przywrócić wartości domyślne wszystkim polom XP?", { tone: "danger", confirmLabel: "Reset" }))) return;
    try {
      const r = await GamificationAdmin.resetConfig();
      setData({ ...data, config: r.config });
      setForm(r.config);
      toast.success("Zresetowano do wartości domyślnych");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Błąd resetu");
    }
  };

  if (loading || !form) return <Spinner text="Ładuję konfigurację…" />;

  // Simulate a mini XP forecast so admin can see impact instantly
  const preview = {
    packages: 100,
    delivered: 100,
    cod: 5000,
    completedRoutes: 2,
  };
  const previewXp =
    Number(form.xp_per_approved_package || 0) * preview.packages +
    Number(form.xp_per_delivered_package || 0) * preview.delivered +
    Number(form.xp_per_cod_pln || 0) * preview.cod +
    Number(form.xp_per_completed_route || 0) * preview.completedRoutes;

  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
      {/* XP levers */}
      <div className="lg:col-span-2 rounded-2xl bg-white ring-1 ring-zinc-200 p-5">
        <div className="flex items-center gap-2 mb-4">
          <Zap className="w-5 h-5 text-amber-500" />
          <h3 className="font-heading font-black uppercase text-lg">Wzory zdobywania XP</h3>
          <button onClick={reset} data-testid="btn-reset-config"
                  className="ml-auto inline-flex items-center gap-1 px-3 py-1.5 rounded-xl text-[11px] font-mono uppercase tracking-widest bg-zinc-100 hover:bg-zinc-200 text-zinc-700 transition-colors">
            <RotateCcw className="w-3.5 h-3.5" /> Reset do domyślnych
          </button>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <XpLever
            label="XP za paczkę zatwierdzoną"
            hint="Przyznawane gdy admin rozliczy zlecenie w Zleceniach"
            value={form.xp_per_approved_package}
            onChange={(v) => setForm({ ...form, xp_per_approved_package: v })}
            testId="xp-per-approved"
            accent="emerald"
          />
          <XpLever
            label="XP za paczkę doręczoną"
            hint="Natychmiastowa nagroda gdy stop = doręczony (nawet przed rozliczeniem)"
            value={form.xp_per_delivered_package}
            onChange={(v) => setForm({ ...form, xp_per_delivered_package: v })}
            testId="xp-per-delivered"
            accent="cyan"
          />
          <XpLever
            label="XP za 1 zł pobrania (COD)"
            hint="Kurier zainkasował 100 zł → dostaje X × 100 XP"
            value={form.xp_per_cod_pln}
            onChange={(v) => setForm({ ...form, xp_per_cod_pln: v })}
            testId="xp-per-cod"
            accent="amber"
          />
          <XpLever
            label="XP za ukończenie trasy 100%"
            hint="Bonus gdy wszystkie paczki na trasie są zatwierdzone"
            value={form.xp_per_completed_route}
            onChange={(v) => setForm({ ...form, xp_per_completed_route: v })}
            testId="xp-per-route"
            accent="purple"
          />
        </div>

        <div className="mt-6 pt-4 border-t border-zinc-100">
          <div className="flex items-center gap-2 mb-3">
            <Trophy className="w-4 h-4 text-purple-600" />
            <h4 className="font-heading font-black uppercase text-sm">Progi poziomów</h4>
            <span className="text-[10px] font-mono text-zinc-500 ml-auto">
              {form.level_thresholds?.length || 0} poziomów
            </span>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
            {(form.level_thresholds || []).map((thr, i) => (
              <div key={i} className="flex items-center gap-2 bg-zinc-50 rounded-xl p-2">
                <div className="w-9 h-9 rounded-lg bg-purple-100 text-purple-700 flex items-center justify-center text-xs font-heading font-black">
                  {i + 1}
                </div>
                <div className="flex-1 grid grid-cols-2 gap-1.5">
                  <input
                    type="number" min={0}
                    value={thr}
                    onChange={(e) => {
                      const arr = [...form.level_thresholds];
                      arr[i] = Number(e.target.value) || 0;
                      setForm({ ...form, level_thresholds: arr });
                    }}
                    data-testid={`lvl-thr-${i}`}
                    className="w-full px-2 py-1.5 rounded-lg border border-zinc-300 text-xs text-right font-mono outline-none focus:ring-2 focus:ring-purple-300"
                  />
                  <input
                    type="text"
                    value={form.level_names?.[i] || ""}
                    onChange={(e) => {
                      const arr = [...(form.level_names || [])];
                      arr[i] = e.target.value;
                      setForm({ ...form, level_names: arr });
                    }}
                    data-testid={`lvl-name-${i}`}
                    className="w-full px-2 py-1.5 rounded-lg border border-zinc-300 text-xs outline-none focus:ring-2 focus:ring-purple-300"
                  />
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="mt-5 flex items-center justify-end gap-2">
          <button onClick={load} className="px-4 py-2 rounded-xl bg-zinc-100 hover:bg-zinc-200 text-xs font-bold uppercase tracking-widest">
            Anuluj
          </button>
          <button onClick={save} disabled={saving} data-testid="btn-save-config"
                  className="inline-flex items-center gap-2 px-5 py-2 rounded-xl bg-zinc-900 text-white hover:bg-brand disabled:opacity-50 text-xs font-bold uppercase tracking-widest">
            <Save className="w-4 h-4" /> {saving ? "Zapisywanie…" : "Zapisz konfigurację"}
          </button>
        </div>
      </div>

      {/* Preview panel */}
      <div className="rounded-2xl bg-gradient-to-br from-purple-600 via-fuchsia-800 to-zinc-950 text-white p-5 ring-1 ring-white/10 shadow-[0_20px_50px_-20px_rgba(0,0,0,0.7)]">
        <div className="flex items-center gap-2 mb-3">
          <BarChart3 className="w-5 h-5" />
          <h3 className="font-heading font-black uppercase text-lg">Symulacja</h3>
        </div>
        <div className="text-[11px] font-mono text-white/70 uppercase tracking-widest mb-3">
          // Kurier który w tym miesiącu:
        </div>
        <div className="text-sm space-y-1 mb-4">
          <div>· zatwierdził <b>{preview.packages}</b> paczek</div>
          <div>· doręczył <b>{preview.delivered}</b> paczek</div>
          <div>· zainkasował <b>{preview.cod.toLocaleString("pl-PL")} zł</b> pobrań</div>
          <div>· ukończył <b>{preview.completedRoutes}</b> tras w 100%</div>
        </div>
        <div className="pt-3 border-t border-white/20">
          <div className="text-[10px] font-mono uppercase tracking-widest text-white/70">
            Zdobyłby łącznie
          </div>
          <div className="text-5xl font-heading font-black tracking-tight mt-1">
            {previewXp.toLocaleString("pl-PL")}
            <span className="text-lg font-mono text-white/60 ml-1">XP</span>
          </div>
        </div>
        <div className="mt-4 text-[10px] font-mono text-white/60 leading-relaxed">
          Zmiany zapisują się od razu — kolejny odczyt dashboardu kuriera używa nowej formuły.
        </div>
      </div>
    </div>
  );
}

function XpLever({ label, hint, value, onChange, testId, accent = "emerald" }) {
  const accents = {
    emerald: "focus:ring-emerald-300 border-emerald-200",
    cyan:    "focus:ring-cyan-300 border-cyan-200",
    amber:   "focus:ring-amber-300 border-amber-200",
    purple:  "focus:ring-purple-300 border-purple-200",
  };
  return (
    <label className="block rounded-xl bg-zinc-50 ring-1 ring-zinc-200 p-3">
      <div className="text-xs font-bold text-zinc-800">{label}</div>
      <div className="text-[10px] font-mono text-zinc-500 mb-2">{hint}</div>
      <div className="flex items-center gap-2">
        <input
          type="number" min={0} step="0.5"
          value={value ?? 0}
          onChange={(e) => onChange(e.target.value === "" ? 0 : Number(e.target.value))}
          data-testid={testId}
          className={`w-full px-3 py-2 rounded-lg border border-zinc-300 text-sm text-right font-mono outline-none focus:ring-2 ${accents[accent]}`}
        />
        <span className="text-[10px] font-mono text-zinc-500 whitespace-nowrap">XP</span>
      </div>
    </label>
  );
}

// =============================================================================
// XP Boost Tab — scheduled multiplier windows
// =============================================================================
const STATUS_META = {
  active:    { label: "AKTYWNY",       tone: "bg-emerald-500 text-white ring-emerald-300" },
  scheduled: { label: "ZAPLANOWANY",   tone: "bg-sky-500 text-white ring-sky-300" },
  ended:     { label: "ZAKOŃCZONY",    tone: "bg-zinc-300 text-zinc-700 ring-zinc-400" },
  disabled:  { label: "WYŁĄCZONY",     tone: "bg-red-100 text-red-700 ring-red-300" },
};

const MULTIPLIER_PRESETS = [1.5, 2, 2.5, 3, 5, 10];

function toDateTimeLocal(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  const off = d.getTimezoneOffset();
  const local = new Date(d.getTime() - off * 60000);
  return local.toISOString().slice(0, 16);
}

function fromDateTimeLocal(v) {
  if (!v) return "";
  return new Date(v).toISOString();
}

function BoostsTab() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const { confirm } = useConfirm();

  const load = () => {
    setLoading(true);
    GamificationAdmin.listBoosts()
      .then((d) => setItems(d.boosts || []))
      .catch(() => toast.error("Nie mogę pobrać boostów"))
      .finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, []);

  const remove = async (b) => {
    if (!(await confirm(`Usunąć boost „${b.name}"?`, { tone: "danger", confirmLabel: "Usuń" }))) return;
    await GamificationAdmin.deleteBoost(b.id);
    toast.success("Usunięto");
    load();
  };
  const toggle = async (b) => {
    await GamificationAdmin.toggleBoost(b.id);
    toast.success(b.active ? "Wyłączono" : "Włączono");
    load();
  };

  const active = items.find((b) => b.derived_status === "active");

  if (loading) return <Spinner text="Ładuję boosty…" />;
  return (
    <div className="space-y-4">
      {/* Currently active hero */}
      <div className={`relative overflow-hidden rounded-2xl p-5 ring-1 shadow-lg transition-colors ${
        active
          ? "bg-gradient-to-br from-orange-500 via-red-700 to-zinc-950 text-white ring-white/10"
          : "bg-white ring-zinc-200"
      }`}>
        <Flame className="absolute -right-6 -bottom-8 w-40 h-40 opacity-15" strokeWidth={1.5} />
        <div className="relative flex items-center gap-3">
          <div className={`w-11 h-11 rounded-xl flex items-center justify-center ring-1 ${
            active ? "bg-white/20 ring-white/30" : "bg-zinc-100 ring-zinc-200"
          }`}>
            <Flame className={`w-5 h-5 ${active ? "text-white" : "text-zinc-500"}`} />
          </div>
          <div className="flex-1">
            <div className={`text-[10px] font-mono uppercase tracking-widest ${
              active ? "text-white/70" : "text-zinc-500"
            }`}>
              // Obecnie {active ? "aktywny boost" : "brak aktywnego boosta"}
            </div>
            <div className="font-heading font-black text-2xl uppercase tracking-tight">
              {active ? (
                <>
                  {active.name} <span className="text-amber-200">×{active.multiplier}</span>
                </>
              ) : "Ustaw pierwszy boost →"}
            </div>
            {active && (
              <div className="text-xs mt-1 opacity-80">
                {new Date(active.starts_at).toLocaleString("pl-PL")} → {new Date(active.ends_at).toLocaleString("pl-PL")}
              </div>
            )}
          </div>
          <button onClick={() => setEditing({})} data-testid="btn-new-boost"
                  className={`inline-flex items-center gap-2 px-4 py-2 rounded-xl text-xs font-bold uppercase tracking-widest transition-colors ${
                    active ? "bg-white text-red-800 hover:bg-orange-100" : "bg-zinc-900 text-white hover:bg-brand"
                  }`}>
            <Plus className="w-4 h-4" /> Nowy boost
          </button>
        </div>
      </div>

      {/* Boost list */}
      <div className="rounded-2xl bg-white ring-1 ring-zinc-200 overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-zinc-50 text-[10px] font-mono uppercase tracking-widest text-zinc-500">
            <tr>
              <th className="text-left px-4 py-2">Nazwa</th>
              <th className="text-center px-4 py-2">Mnożnik</th>
              <th className="text-left px-4 py-2">Start</th>
              <th className="text-left px-4 py-2">Koniec</th>
              <th className="text-center px-4 py-2">Status</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {items.map((b) => {
              const st = STATUS_META[b.derived_status] || STATUS_META.disabled;
              return (
                <tr key={b.id} className="border-t border-zinc-100 hover:bg-zinc-50"
                    data-testid={`boost-row-${b.id}`}>
                  <td className="px-4 py-2 font-semibold">
                    <div className="inline-flex items-center gap-2">
                      <Flame className="w-3.5 h-3.5 text-red-500" />
                      {b.name}
                    </div>
                  </td>
                  <td className="px-4 py-2 text-center">
                    <span className="inline-flex items-center px-3 py-1 rounded-full bg-amber-100 text-amber-800 font-heading font-black">
                      ×{b.multiplier}
                    </span>
                  </td>
                  <td className="px-4 py-2 text-[11px] font-mono text-zinc-600">
                    {new Date(b.starts_at).toLocaleString("pl-PL")}
                  </td>
                  <td className="px-4 py-2 text-[11px] font-mono text-zinc-600">
                    {new Date(b.ends_at).toLocaleString("pl-PL")}
                  </td>
                  <td className="px-4 py-2 text-center">
                    <span className={`inline-block px-2 py-1 rounded-full text-[10px] font-mono uppercase tracking-widest ring-1 ${st.tone}`}>
                      {st.label}
                    </span>
                  </td>
                  <td className="px-4 py-2 text-right whitespace-nowrap">
                    <button onClick={() => toggle(b)} data-testid={`btn-toggle-boost-${b.id}`}
                            className="p-1.5 rounded hover:bg-zinc-200 text-zinc-600" title={b.active ? "Wyłącz" : "Włącz"}>
                      {b.active ? <ToggleRight className="w-5 h-5 text-emerald-600" /> : <ToggleLeft className="w-5 h-5" />}
                    </button>
                    <button onClick={() => setEditing(b)} className="p-1.5 rounded hover:bg-zinc-200 text-zinc-600"
                            data-testid={`btn-edit-boost-${b.id}`}>
                      <Edit3 className="w-4 h-4" />
                    </button>
                    <button onClick={() => remove(b)} className="p-1.5 rounded hover:bg-red-100 text-red-600"
                            data-testid={`btn-del-boost-${b.id}`}>
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </td>
                </tr>
              );
            })}
            {!items.length && (
              <tr><td colSpan={6} className="px-4 py-8 text-center text-zinc-500">
                Brak zaplanowanych boostów. Kliknij „Nowy boost&rdquo; żeby dodać pierwszy.
              </td></tr>
            )}
          </tbody>
        </table>
      </div>

      {editing !== null && (
        <BoostEditModal boost={editing} onClose={() => setEditing(null)}
                        onSaved={() => { setEditing(null); load(); }} />
      )}
    </div>
  );
}

function BoostEditModal({ boost, onClose, onSaved }) {
  const isNew = !boost.id;
  const nowLocal = toDateTimeLocal(new Date().toISOString());
  const [form, setForm] = useState({
    name: boost.name || "",
    multiplier: boost.multiplier || 2,
    starts_at: boost.starts_at ? toDateTimeLocal(boost.starts_at) : nowLocal,
    ends_at: boost.ends_at ? toDateTimeLocal(boost.ends_at) : "",
    active: boost.active !== false,
  });

  const setPreset = (hours) => {
    const start = new Date();
    const end = new Date(start.getTime() + hours * 3600 * 1000);
    setForm({
      ...form,
      starts_at: toDateTimeLocal(start.toISOString()),
      ends_at: toDateTimeLocal(end.toISOString()),
    });
  };
  const setWeekend = () => {
    const now = new Date();
    const day = now.getDay(); // 0=sun, 6=sat
    const daysToSat = (6 - day + 7) % 7 || 7;
    const start = new Date(now);
    start.setDate(now.getDate() + daysToSat);
    start.setHours(0, 0, 0, 0);
    const end = new Date(start);
    end.setDate(start.getDate() + 2); // Sat 00:00 → Mon 00:00
    setForm({
      ...form,
      starts_at: toDateTimeLocal(start.toISOString()),
      ends_at: toDateTimeLocal(end.toISOString()),
    });
  };

  const save = async () => {
    if (!form.name?.trim()) { toast.error("Podaj nazwę boosta"); return; }
    if (!form.starts_at || !form.ends_at) { toast.error("Podaj daty start/koniec"); return; }
    const payload = {
      name: form.name.trim(),
      multiplier: Number(form.multiplier),
      starts_at: fromDateTimeLocal(form.starts_at),
      ends_at: fromDateTimeLocal(form.ends_at),
      active: !!form.active,
    };
    try {
      if (isNew) await GamificationAdmin.createBoost(payload);
      else await GamificationAdmin.updateBoost(boost.id, payload);
      toast.success(isNew ? "Boost utworzony" : "Boost zapisany");
      onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Błąd zapisu");
    }
  };

  return (
    <Modal title={isNew ? "Nowy XP Boost" : `Edytuj: ${boost.name}`} onClose={onClose} testId="modal-boost">
      <div className="space-y-4">
        <FieldInput label="Nazwa boosta" value={form.name}
                    onChange={(v) => setForm({ ...form, name: v })}
                    hint={`np. "Weekend Fever", "Piątkowy szał"`} />

        <div>
          <div className="text-[10px] font-mono uppercase tracking-widest text-zinc-500 mb-2">Mnożnik</div>
          <div className="flex gap-2 flex-wrap">
            {MULTIPLIER_PRESETS.map((m) => (
              <button key={m}
                      onClick={() => setForm({ ...form, multiplier: m })}
                      data-testid={`btn-mult-${m}`}
                      className={`px-4 py-2 rounded-xl font-heading font-black text-lg transition-colors ${
                        Number(form.multiplier) === m
                          ? "bg-amber-500 text-white shadow-lg scale-105"
                          : "bg-amber-50 text-amber-800 ring-1 ring-amber-200 hover:bg-amber-100"
                      }`}>
                ×{m}
              </button>
            ))}
            <input type="number" min={1} max={20} step="0.5" value={form.multiplier}
                   onChange={(e) => setForm({ ...form, multiplier: Number(e.target.value) })}
                   data-testid="input-mult"
                   className="w-24 px-3 py-2 rounded-xl border border-zinc-300 text-lg font-mono text-right outline-none focus:ring-2 focus:ring-amber-300" />
          </div>
        </div>

        <div>
          <div className="text-[10px] font-mono uppercase tracking-widest text-zinc-500 mb-2">Szybkie ustawienia</div>
          <div className="flex gap-2 flex-wrap">
            <PresetBtn onClick={() => setPreset(1)}>Na 1h</PresetBtn>
            <PresetBtn onClick={() => setPreset(24)}>Na 24h</PresetBtn>
            <PresetBtn onClick={() => setPreset(72)}>Na 3 dni</PresetBtn>
            <PresetBtn onClick={() => setPreset(168)}>Na 7 dni</PresetBtn>
            <PresetBtn onClick={setWeekend}>
              <Calendar className="w-3.5 h-3.5" /> Najbliższy weekend
            </PresetBtn>
          </div>
        </div>

        <div className="grid grid-cols-2 gap-3">
          <FieldInput label="Start" type="datetime-local" value={form.starts_at}
                      onChange={(v) => setForm({ ...form, starts_at: v })}
                      testId="input-start" />
          <FieldInput label="Koniec" type="datetime-local" value={form.ends_at}
                      onChange={(v) => setForm({ ...form, ends_at: v })}
                      testId="input-end" />
        </div>

        <label className="flex items-center gap-2">
          <input type="checkbox" checked={form.active}
                 onChange={(e) => setForm({ ...form, active: e.target.checked })}
                 data-testid="chk-boost-active" />
          <span className="text-sm">Włącz od razu po zapisie</span>
        </label>
      </div>

      <div className="flex justify-end gap-2 mt-5">
        <button onClick={onClose} className="px-4 py-2 rounded-xl bg-zinc-100 hover:bg-zinc-200 text-sm font-bold uppercase tracking-widest">
          Anuluj
        </button>
        <button onClick={save} data-testid="btn-save-boost"
                className="inline-flex items-center gap-2 px-5 py-2 rounded-xl bg-gradient-to-r from-orange-500 to-red-700 hover:from-orange-600 hover:to-red-800 text-white text-xs font-bold uppercase tracking-widest">
          <PlayCircle className="w-4 h-4" /> {isNew ? "Utwórz boost" : "Zapisz"}
        </button>
      </div>
    </Modal>
  );
}

function PresetBtn({ onClick, children }) {
  return (
    <button onClick={onClick}
            className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full text-[11px] font-mono uppercase tracking-widest bg-zinc-100 hover:bg-zinc-200 text-zinc-700 transition-colors">
      {children}
    </button>
  );
}

// =============================================================================
// Achievements Tab
// =============================================================================
function AchievementsTab() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // null | {} | ach
  const { confirm } = useConfirm();

  const load = () => GamificationAdmin.listAchievements()
    .then((d) => setItems(d.achievements || []))
    .catch(() => toast.error("Nie mogę pobrać osiągnięć"))
    .finally(() => setLoading(false));

  useEffect(() => { load(); }, []);

  const remove = async (a) => {
    if (!(await confirm(`Usunąć osiągnięcie „${a.name}"? Usunie się też u wszystkich użytkowników.`, { tone: "danger", confirmLabel: "Usuń" }))) return;
    await GamificationAdmin.deleteAchievement(a.code);
    toast.success("Usunięto");
    load();
  };

  if (loading) return <Spinner text="Ładuję osiągnięcia…" />;
  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <h2 className="font-heading font-black uppercase text-lg text-zinc-900">
          Katalog osiągnięć ({items.length})
        </h2>
        <button
          onClick={() => setEditing({})}
          data-testid="btn-new-achievement"
          className="inline-flex items-center gap-2 px-4 py-2 rounded-xl bg-zinc-900 text-white hover:bg-brand transition-colors text-xs font-bold uppercase tracking-widest"
        >
          <Plus className="w-4 h-4" /> Nowe osiągnięcie
        </button>
      </div>
      <div className="rounded-2xl bg-white ring-1 ring-zinc-200 overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-zinc-50 text-[10px] font-mono uppercase tracking-widest text-zinc-500">
            <tr>
              <th className="text-left px-4 py-2">Kod</th>
              <th className="text-left px-4 py-2">Nazwa</th>
              <th className="text-left px-4 py-2">Warunek</th>
              <th className="text-right px-4 py-2">XP</th>
              <th className="text-right px-4 py-2">💎</th>
              <th className="text-center px-4 py-2">Aktywne</th>
              <th className="px-4 py-2"></th>
            </tr>
          </thead>
          <tbody>
            {items.map((a) => (
              <tr key={a.code} className="border-t border-zinc-100 hover:bg-zinc-50">
                <td className="px-4 py-2 font-mono text-[10px] text-zinc-500">{a.code}</td>
                <td className="px-4 py-2 font-semibold">{a.name}</td>
                <td className="px-4 py-2 text-xs text-zinc-600">
                  {conditionLabel(a.condition_type)} {a.condition_type !== "manual" && <b>{a.condition_value}</b>}
                </td>
                <td className="px-4 py-2 text-right"><Zap className="w-3 h-3 inline text-amber-500" /> {a.xp_reward}</td>
                <td className="px-4 py-2 text-right"><Gem className="w-3 h-3 inline text-cyan-500" /> {a.diamond_reward}</td>
                <td className="px-4 py-2 text-center">
                  {a.active !== false
                    ? <CheckCircle2 className="w-4 h-4 text-emerald-500 inline" />
                    : <XCircle className="w-4 h-4 text-zinc-400 inline" />}
                </td>
                <td className="px-4 py-2 text-right whitespace-nowrap">
                  <button onClick={() => setEditing(a)} data-testid={`btn-edit-ach-${a.code}`}
                          className="p-1.5 rounded hover:bg-zinc-200 text-zinc-600">
                    <Edit3 className="w-4 h-4" />
                  </button>
                  <button onClick={() => remove(a)} data-testid={`btn-del-ach-${a.code}`}
                          className="p-1.5 rounded hover:bg-red-100 text-red-600">
                    <Trash2 className="w-4 h-4" />
                  </button>
                </td>
              </tr>
            ))}
            {!items.length && (
              <tr><td colSpan={7} className="px-4 py-8 text-center text-zinc-500">Brak osiągnięć.</td></tr>
            )}
          </tbody>
        </table>
      </div>
      {editing !== null && (
        <AchievementEditModal ach={editing} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); load(); }} />
      )}
    </div>
  );
}

function conditionLabel(type) {
  return CONDITION_TYPES.find((c) => c.value === type)?.label || type;
}

function AchievementEditModal({ ach, onClose, onSaved }) {
  const isNew = !ach.code;
  const [form, setForm] = useState({
    code: ach.code || "",
    name: ach.name || "",
    description: ach.description || "",
    icon: ach.icon || "star",
    category: ach.category || "custom",
    condition_type: ach.condition_type || "packages_total",
    condition_value: ach.condition_value ?? 0,
    xp_reward: ach.xp_reward ?? 0,
    diamond_reward: ach.diamond_reward ?? 0,
    active: ach.active !== false,
  });
  const save = async () => {
    try {
      if (isNew) await GamificationAdmin.createAchievement(form);
      else await GamificationAdmin.updateAchievement(ach.code, form);
      toast.success(isNew ? "Utworzono" : "Zapisano");
      onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Błąd zapisu");
    }
  };
  return (
    <Modal title={isNew ? "Nowe osiągnięcie" : `Edytuj: ${ach.name}`} onClose={onClose} testId="modal-achievement">
      <div className="grid grid-cols-2 gap-3">
        <FieldInput label="Kod" value={form.code} onChange={(v) => setForm({ ...form, code: v })} disabled={!isNew} />
        <FieldInput label="Ikona" value={form.icon} onChange={(v) => setForm({ ...form, icon: v })}
                    hint="star | package | zap | wallet | crown | target | shield | sparkles | flame | moon | trophy | award" />
        <FieldInput label="Nazwa" value={form.name} onChange={(v) => setForm({ ...form, name: v })} className="col-span-2" />
        <FieldInput label="Opis" value={form.description} onChange={(v) => setForm({ ...form, description: v })} className="col-span-2" />
        <FieldSelect label="Kategoria" value={form.category} onChange={(v) => setForm({ ...form, category: v })}
                     options={[
                       { value: "milestone", label: "Kamienie milowe" },
                       { value: "weekly", label: "Tygodniowe" },
                       { value: "cod", label: "Pobrania" },
                       { value: "routes", label: "Trasy" },
                       { value: "quality", label: "Jakość" },
                       { value: "streak", label: "Passa" },
                       { value: "special", label: "Specjalne" },
                       { value: "custom", label: "Inne" },
                     ]} />
        <FieldSelect label="Typ warunku" value={form.condition_type} onChange={(v) => setForm({ ...form, condition_type: v })}
                     options={CONDITION_TYPES} />
        {form.condition_type !== "manual" && (
          <FieldInput label="Wartość warunku" type="number" value={form.condition_value}
                      onChange={(v) => setForm({ ...form, condition_value: Number(v) })} />
        )}
        <FieldInput label="Nagroda XP" type="number" value={form.xp_reward}
                    onChange={(v) => setForm({ ...form, xp_reward: Number(v) })} />
        <FieldInput label="Nagroda 💎" type="number" value={form.diamond_reward}
                    onChange={(v) => setForm({ ...form, diamond_reward: Number(v) })} />
        <label className="flex items-center gap-2 col-span-2 mt-2">
          <input type="checkbox" checked={form.active} onChange={(e) => setForm({ ...form, active: e.target.checked })}
                 data-testid="chk-ach-active" />
          <span className="text-sm">Aktywne</span>
        </label>
      </div>
      <div className="flex justify-end gap-2 mt-4">
        <button onClick={onClose} className="px-4 py-2 rounded-xl bg-zinc-100 hover:bg-zinc-200 text-sm font-bold uppercase tracking-widest">
          Anuluj
        </button>
        <button onClick={save} data-testid="btn-save-ach"
                className="inline-flex items-center gap-2 px-4 py-2 rounded-xl bg-zinc-900 text-white hover:bg-brand text-xs font-bold uppercase tracking-widest">
          <Save className="w-4 h-4" /> Zapisz
        </button>
      </div>
    </Modal>
  );
}

// =============================================================================
// Shop Tab
// =============================================================================
function ShopTab() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const { confirm } = useConfirm();

  const load = () => GamificationAdmin.listShop()
    .then((d) => setItems(d.items || []))
    .catch(() => toast.error("Błąd sklepu"))
    .finally(() => setLoading(false));
  useEffect(() => { load(); }, []);

  const remove = async (i) => {
    if (!(await confirm(`Usunąć produkt „${i.name}"?`, { tone: "danger", confirmLabel: "Usuń" }))) return;
    await GamificationAdmin.deleteItem(i.id);
    toast.success("Usunięto");
    load();
  };

  if (loading) return <Spinner text="Ładuję sklep…" />;
  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <h2 className="font-heading font-black uppercase text-lg">Sklep GoPossible ({items.length})</h2>
        <button onClick={() => setEditing({})} data-testid="btn-new-shop-item"
                className="inline-flex items-center gap-2 px-4 py-2 rounded-xl bg-zinc-900 text-white hover:bg-brand text-xs font-bold uppercase tracking-widest">
          <Plus className="w-4 h-4" /> Nowy produkt
        </button>
      </div>
      <div className="rounded-2xl bg-white ring-1 ring-zinc-200 overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-zinc-50 text-[10px] font-mono uppercase tracking-widest text-zinc-500">
            <tr>
              <th className="text-left px-4 py-2">Kod</th>
              <th className="text-left px-4 py-2">Nazwa</th>
              <th className="text-left px-4 py-2">Kategoria</th>
              <th className="text-right px-4 py-2">Cena</th>
              <th className="text-right px-4 py-2">Stan</th>
              <th className="text-center px-4 py-2">Aktywne</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {items.map((i) => (
              <tr key={i.id} className="border-t border-zinc-100 hover:bg-zinc-50">
                <td className="px-4 py-2 font-mono text-[10px] text-zinc-500">{i.code}</td>
                <td className="px-4 py-2 font-semibold">{i.name}</td>
                <td className="px-4 py-2 text-xs">{SHOP_CATEGORIES.find((c) => c.value === i.category)?.label || i.category}</td>
                <td className="px-4 py-2 text-right"><Gem className="w-3 h-3 inline text-cyan-500" /> {i.price_diamonds}</td>
                <td className="px-4 py-2 text-right">{i.stock === null || i.stock === undefined ? "∞" : i.stock}</td>
                <td className="px-4 py-2 text-center">
                  {i.active !== false
                    ? <CheckCircle2 className="w-4 h-4 text-emerald-500 inline" />
                    : <XCircle className="w-4 h-4 text-zinc-400 inline" />}
                </td>
                <td className="px-4 py-2 text-right whitespace-nowrap">
                  <button onClick={() => setEditing(i)} className="p-1.5 rounded hover:bg-zinc-200 text-zinc-600"
                          data-testid={`btn-edit-item-${i.code}`}>
                    <Edit3 className="w-4 h-4" />
                  </button>
                  <button onClick={() => remove(i)} className="p-1.5 rounded hover:bg-red-100 text-red-600"
                          data-testid={`btn-del-item-${i.code}`}>
                    <Trash2 className="w-4 h-4" />
                  </button>
                </td>
              </tr>
            ))}
            {!items.length && (
              <tr><td colSpan={7} className="px-4 py-8 text-center text-zinc-500">Brak produktów.</td></tr>
            )}
          </tbody>
        </table>
      </div>
      {editing !== null && (
        <ShopEditModal item={editing} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); load(); }} />
      )}
    </div>
  );
}

function ShopEditModal({ item, onClose, onSaved }) {
  const isNew = !item.id;
  const [form, setForm] = useState({
    code: item.code || "",
    name: item.name || "",
    description: item.description || "",
    icon: item.icon || "gift",
    category: item.category || "gadget",
    price_diamonds: item.price_diamonds ?? 100,
    stock: item.stock === null || item.stock === undefined ? "" : item.stock,
    requires_approval: item.requires_approval !== false,
    active: item.active !== false,
  });
  const save = async () => {
    const payload = {
      ...form,
      stock: form.stock === "" || form.stock === null ? null : Number(form.stock),
      price_diamonds: Number(form.price_diamonds),
    };
    try {
      if (isNew) await GamificationAdmin.createItem(payload);
      else await GamificationAdmin.updateItem(item.id, payload);
      toast.success(isNew ? "Utworzono" : "Zapisano");
      onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Błąd zapisu");
    }
  };
  return (
    <Modal title={isNew ? "Nowy produkt" : `Edytuj: ${item.name}`} onClose={onClose} testId="modal-shop-item">
      <div className="grid grid-cols-2 gap-3">
        <FieldInput label="Kod" value={form.code} onChange={(v) => setForm({ ...form, code: v })} disabled={!isNew} />
        <FieldInput label="Ikona" value={form.icon} onChange={(v) => setForm({ ...form, icon: v })}
                    hint="headphones | beach | zap | trending | fuel | gift | coffee | shirt | battery" />
        <FieldInput label="Nazwa" value={form.name} onChange={(v) => setForm({ ...form, name: v })} className="col-span-2" />
        <FieldInput label="Opis" value={form.description} onChange={(v) => setForm({ ...form, description: v })} className="col-span-2" />
        <FieldSelect label="Kategoria" value={form.category} onChange={(v) => setForm({ ...form, category: v })}
                     options={SHOP_CATEGORIES} />
        <FieldInput label="Cena (💎)" type="number" value={form.price_diamonds}
                    onChange={(v) => setForm({ ...form, price_diamonds: Number(v) })} />
        <FieldInput label="Stan magazynowy (puste = ∞)" type="number" value={form.stock}
                    onChange={(v) => setForm({ ...form, stock: v })} />
        <label className="flex items-center gap-2 mt-6">
          <input type="checkbox" checked={form.active} onChange={(e) => setForm({ ...form, active: e.target.checked })} />
          <span className="text-sm">Aktywny</span>
        </label>
      </div>
      <div className="flex justify-end gap-2 mt-4">
        <button onClick={onClose} className="px-4 py-2 rounded-xl bg-zinc-100 hover:bg-zinc-200 text-sm font-bold uppercase tracking-widest">
          Anuluj
        </button>
        <button onClick={save} data-testid="btn-save-item"
                className="inline-flex items-center gap-2 px-4 py-2 rounded-xl bg-zinc-900 text-white hover:bg-brand text-xs font-bold uppercase tracking-widest">
          <Save className="w-4 h-4" /> Zapisz
        </button>
      </div>
    </Modal>
  );
}

// =============================================================================
// Orders Tab
// =============================================================================
function OrdersTab() {
  const [orders, setOrders] = useState([]);
  const [status, setStatus] = useState("pending");
  const [loading, setLoading] = useState(true);
  const { promptInput, confirm } = useConfirm();

  const load = () => GamificationAdmin.listOrders(status)
    .then((d) => setOrders(d.orders || []))
    .catch(() => toast.error("Błąd listy zamówień"))
    .finally(() => setLoading(false));

  useEffect(() => { setLoading(true); load(); }, [status]); // eslint-disable-line react-hooks/exhaustive-deps

  const approve = async (o) => {
    const note = await promptInput(`Notatka do „${o.item_name}" (opcjonalna)`, "");
    if (note === null) return;
    await GamificationAdmin.approveOrder(o.id, note || "");
    toast.success("Zaakceptowano");
    load();
  };
  const reject = async (o) => {
    if (!(await confirm(`Odrzucić „${o.item_name}"? Diamenty wrócą do kuriera.`, { tone: "danger", confirmLabel: "Odrzuć" }))) return;
    const note = await promptInput("Powód odrzucenia (opcjonalny)", "");
    if (note === null) return;
    await GamificationAdmin.rejectOrder(o.id, note || "");
    toast.success("Odrzucono i zwrócono diamenty");
    load();
  };
  const deliver = async (o) => {
    if (!(await confirm(`Oznaczyć „${o.item_name}" jako wydane?`, { confirmLabel: "Wydaj", tone: "info" }))) return;
    await GamificationAdmin.deliverOrder(o.id);
    toast.success("Oznaczone jako wydane");
    load();
  };

  return (
    <div className="space-y-3">
      <div className="flex items-center gap-2">
        <Filter className="w-4 h-4 text-zinc-500" />
        <div className="text-xs font-mono uppercase tracking-widest text-zinc-500 mr-3">Status</div>
        {ORDER_STATUSES.map((s) => (
          <button key={s.value}
                  onClick={() => setStatus(s.value)}
                  data-testid={`filter-status-${s.value || "all"}`}
                  className={`px-3 py-1.5 rounded-full text-[11px] font-mono uppercase tracking-widest transition-colors ${
                    status === s.value ? "bg-zinc-900 text-white" : "bg-zinc-100 hover:bg-zinc-200"
                  }`}>
            {s.label}
          </button>
        ))}
      </div>
      {loading ? <Spinner text="Ładuję zamówienia…" /> : (
        <div className="rounded-2xl bg-white ring-1 ring-zinc-200 overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-zinc-50 text-[10px] font-mono uppercase tracking-widest text-zinc-500">
              <tr>
                <th className="text-left px-4 py-2">Kurier</th>
                <th className="text-left px-4 py-2">Produkt</th>
                <th className="text-right px-4 py-2">💎</th>
                <th className="text-left px-4 py-2">Data</th>
                <th className="text-center px-4 py-2">Status</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {orders.map((o) => (
                <tr key={o.id} className="border-t border-zinc-100 hover:bg-zinc-50">
                  <td className="px-4 py-2">
                    <div className="font-semibold">{o.user_name}</div>
                    <div className="text-[10px] font-mono text-zinc-500">{o.user_email}</div>
                  </td>
                  <td className="px-4 py-2">
                    <div className="font-semibold">{o.item_name}</div>
                    {o.admin_note && <div className="text-[10px] italic text-zinc-500">„{o.admin_note}&rdquo;</div>}
                  </td>
                  <td className="px-4 py-2 text-right"><Gem className="w-3 h-3 inline text-cyan-500" /> {o.price_paid}</td>
                  <td className="px-4 py-2 text-[11px] font-mono text-zinc-500">
                    {new Date(o.requested_at).toLocaleString("pl-PL")}
                  </td>
                  <td className="px-4 py-2 text-center">
                    <StatusPill status={o.status} />
                  </td>
                  <td className="px-4 py-2 text-right whitespace-nowrap">
                    {o.status === "pending" && (
                      <>
                        <button onClick={() => approve(o)} data-testid={`btn-approve-${o.id}`}
                                className="p-1.5 rounded hover:bg-emerald-100 text-emerald-600" title="Zaakceptuj">
                          <CheckCircle2 className="w-4 h-4" />
                        </button>
                        <button onClick={() => reject(o)} data-testid={`btn-reject-${o.id}`}
                                className="p-1.5 rounded hover:bg-red-100 text-red-600" title="Odrzuć">
                          <XCircle className="w-4 h-4" />
                        </button>
                      </>
                    )}
                    {(o.status === "pending" || o.status === "approved") && (
                      <button onClick={() => deliver(o)} data-testid={`btn-deliver-${o.id}`}
                              className="p-1.5 rounded hover:bg-sky-100 text-sky-600" title="Oznacz jako wydane">
                        <Truck className="w-4 h-4" />
                      </button>
                    )}
                  </td>
                </tr>
              ))}
              {!orders.length && (
                <tr><td colSpan={6} className="px-4 py-8 text-center text-zinc-500">Brak zamówień.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function StatusPill({ status }) {
  const map = {
    pending:   { label: "Oczekuje",     tone: "bg-amber-100 text-amber-800",       icon: null },
    approved:  { label: "Zaakceptowane",tone: "bg-sky-100 text-sky-800",           icon: null },
    delivered: { label: "Wydane",       tone: "bg-emerald-100 text-emerald-800",   icon: null },
    rejected:  { label: "Odrzucone",    tone: "bg-red-100 text-red-800",           icon: null },
  };
  const m = map[status] || { label: status, tone: "bg-zinc-100 text-zinc-800" };
  return (
    <span className={`inline-block px-2 py-1 rounded-full text-[10px] font-mono uppercase tracking-widest ${m.tone}`}>
      {m.label}
    </span>
  );
}

// =============================================================================
// Grants Tab (manual)
// =============================================================================
function GrantsTab() {
  const [users, setUsers] = useState([]);
  const [achs, setAchs] = useState([]);
  const [selUser, setSelUser] = useState("");
  const [amount, setAmount] = useState(50);
  const [reason, setReason] = useState("");
  const [selAch, setSelAch] = useState("");

  useEffect(() => {
    Promise.all([GamificationAdmin.users(), GamificationAdmin.listAchievements()])
      .then(([u, a]) => {
        setUsers(u.users || []);
        setAchs(a.achievements || []);
      })
      .catch(() => toast.error("Błąd ładowania"));
  }, []);

  const grantDiamonds = async () => {
    if (!selUser || !amount) { toast.error("Wybierz użytkownika i kwotę"); return; }
    try {
      const r = await GamificationAdmin.grantDiamonds(selUser, Number(amount), reason || "Bonus od administratora");
      toast.success(`Nowy balans: ${r.new_balance}💎`);
      setAmount(50); setReason("");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Błąd");
    }
  };

  const grantAchievement = async () => {
    if (!selUser || !selAch) { toast.error("Wybierz użytkownika i osiągnięcie"); return; }
    try {
      await GamificationAdmin.grantAchievement(selUser, selAch);
      toast.success("Odblokowano osiągnięcie (nagrody dopisano)");
      setSelAch("");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Błąd");
    }
  };

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
      <div className="rounded-2xl bg-white ring-1 ring-zinc-200 p-5">
        <div className="flex items-center gap-2 mb-3">
          <Coins className="w-5 h-5 text-cyan-600" />
          <h3 className="font-heading font-black uppercase text-lg">Przyznaj diamenty</h3>
        </div>
        <div className="space-y-3">
          <FieldSelect label="Użytkownik" value={selUser} onChange={setSelUser}
                       options={[{ value: "", label: "— wybierz —" },
                                 ...users.map((u) => ({ value: u.id, label: `${u.name || u.email} (${u.role})` }))]} />
          <FieldInput label="Kwota (może być ujemna)" type="number" value={amount} onChange={(v) => setAmount(v)} />
          <FieldInput label="Powód" value={reason} onChange={setReason} />
          <button onClick={grantDiamonds} data-testid="btn-grant-diamonds"
                  className="w-full inline-flex items-center justify-center gap-2 px-4 py-2 rounded-xl bg-cyan-600 text-white hover:bg-cyan-700 text-xs font-bold uppercase tracking-widest">
            <Gem className="w-4 h-4" /> Przyznaj / potrąć
          </button>
        </div>
      </div>
      <div className="rounded-2xl bg-white ring-1 ring-zinc-200 p-5">
        <div className="flex items-center gap-2 mb-3">
          <Award className="w-5 h-5 text-purple-600" />
          <h3 className="font-heading font-black uppercase text-lg">Odblokuj osiągnięcie</h3>
        </div>
        <div className="space-y-3">
          <FieldSelect label="Użytkownik" value={selUser} onChange={setSelUser}
                       options={[{ value: "", label: "— wybierz —" },
                                 ...users.map((u) => ({ value: u.id, label: `${u.name || u.email} (${u.role})` }))]} />
          <FieldSelect label="Osiągnięcie" value={selAch} onChange={setSelAch}
                       options={[{ value: "", label: "— wybierz —" },
                                 ...achs.map((a) => ({ value: a.code, label: `${a.name} · +${a.xp_reward}XP / +${a.diamond_reward}💎` }))]} />
          <div className="text-[11px] text-zinc-500 font-mono">
            Ręczne odblokowanie od razu dopisze XP i diamenty w portfelu użytkownika.
          </div>
          <button onClick={grantAchievement} data-testid="btn-grant-achievement"
                  className="w-full inline-flex items-center justify-center gap-2 px-4 py-2 rounded-xl bg-purple-600 text-white hover:bg-purple-700 text-xs font-bold uppercase tracking-widest">
            <Star className="w-4 h-4" /> Przyznaj osiągnięcie
          </button>
        </div>
      </div>
    </div>
  );
}

// =============================================================================
// Stats Tab
// =============================================================================
function StatsTab() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    GamificationAdmin.stats()
      .then(setData)
      .catch(() => toast.error("Błąd statystyk"))
      .finally(() => setLoading(false));
  }, []);
  if (loading) return <Spinner text="Ładuję statystyki…" />;
  if (!data) return null;
  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
      {/* Top drivers */}
      <Card title="Top posiadacze diamentów" icon={Users}>
        <div className="divide-y divide-zinc-100">
          {(data.top_drivers || []).slice(0, 15).map((d, i) => (
            <div key={d.id} className="flex items-center gap-3 py-2">
              <div className={`w-7 h-7 rounded-lg flex items-center justify-center text-xs font-heading font-black ${
                i === 0 ? "bg-amber-100 text-amber-700" :
                i === 1 ? "bg-zinc-100 text-zinc-700" :
                i === 2 ? "bg-orange-100 text-orange-700" : "bg-zinc-50 text-zinc-500"
              }`}>{i + 1}</div>
              <div className="flex-1 min-w-0">
                <div className="text-sm font-semibold truncate">{d.name || d.email}</div>
                <div className="text-[10px] font-mono text-zinc-500">{d.role}</div>
              </div>
              <div className="inline-flex items-center gap-1 text-sm font-heading font-black text-cyan-700">
                <Gem className="w-4 h-4" /> {d.balance.toLocaleString("pl-PL")}
              </div>
            </div>
          ))}
          {!data.top_drivers?.length && <div className="text-sm text-zinc-500 py-4">Brak danych.</div>}
        </div>
      </Card>
      {/* Orders summary */}
      <Card title="Zamówienia — status" icon={Package}>
        <div className="grid grid-cols-2 gap-3">
          {Object.entries(data.orders_by_status || {}).map(([s, v]) => (
            <div key={s} className="rounded-xl bg-zinc-50 ring-1 ring-zinc-200 p-3">
              <StatusPill status={s} />
              <div className="text-2xl font-heading font-black mt-2">{v.count}</div>
              <div className="text-[10px] font-mono text-zinc-500">za {v.diamonds}💎 łącznie</div>
            </div>
          ))}
          {!Object.keys(data.orders_by_status || {}).length && <div className="text-sm text-zinc-500">Brak zamówień.</div>}
        </div>
      </Card>
      {/* Top items */}
      <Card title="Najchętniej kupowane" icon={ShoppingBag}>
        <div className="divide-y divide-zinc-100">
          {(data.top_items || []).map((it) => (
            <div key={it._id} className="flex items-center py-2">
              <div className="flex-1 truncate">{it._id}</div>
              <div className="text-sm font-bold text-zinc-700">{it.n}×</div>
            </div>
          ))}
          {!data.top_items?.length && <div className="text-sm text-zinc-500 py-4">Brak sprzedaży.</div>}
        </div>
      </Card>
      {/* Achievement counts */}
      <Card title="Odblokowania osiągnięć" icon={Trophy}>
        <div className="divide-y divide-zinc-100">
          {(data.achievement_counts || []).map((a) => (
            <div key={a.code} className="flex items-center py-2">
              <div className="flex-1 truncate text-sm">{a.name}</div>
              <div className="text-sm font-bold text-purple-700">{a.count}×</div>
            </div>
          ))}
          {!data.achievement_counts?.length && <div className="text-sm text-zinc-500 py-4">Nikt jeszcze nic nie odblokował.</div>}
        </div>
      </Card>
    </div>
  );
}

function Card({ title, icon: Icon, children }) {
  return (
    <div className="rounded-2xl bg-white ring-1 ring-zinc-200 p-5">
      <div className="flex items-center gap-2 mb-3">
        <Icon className="w-4 h-4 text-zinc-500" />
        <h3 className="font-heading font-black uppercase text-base">{title}</h3>
      </div>
      {children}
    </div>
  );
}

// =============================================================================
// Small helpers
// =============================================================================
function Modal({ title, onClose, children, testId }) {
  return (
    <div className="fixed inset-0 z-[1400] bg-black/60 backdrop-blur-sm flex items-center justify-center p-4"
         onClick={onClose}>
      <div className="bg-white rounded-2xl max-w-2xl w-full p-5 shadow-2xl" data-testid={testId}
           onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-heading font-black uppercase text-lg">{title}</h3>
          <button onClick={onClose} className="w-8 h-8 rounded-lg hover:bg-zinc-100 flex items-center justify-center">
            <X className="w-4 h-4" />
          </button>
        </div>
        {children}
      </div>
    </div>
  );
}

function FieldInput({ label, value, onChange, type = "text", disabled = false, hint = "", className = "" }) {
  return (
    <label className={`block ${className}`}>
      <span className="text-[10px] font-mono uppercase tracking-widest text-zinc-500">{label}</span>
      <input type={type} value={value ?? ""} disabled={disabled}
             onChange={(e) => onChange(e.target.value)}
             className="w-full mt-1 px-3 py-2 rounded-xl border border-zinc-200 focus:border-zinc-900 outline-none text-sm disabled:bg-zinc-50 disabled:text-zinc-500" />
      {hint && <div className="text-[10px] text-zinc-400 mt-0.5">{hint}</div>}
    </label>
  );
}

function FieldSelect({ label, value, onChange, options }) {
  return (
    <label className="block">
      <span className="text-[10px] font-mono uppercase tracking-widest text-zinc-500">{label}</span>
      <select value={value ?? ""} onChange={(e) => onChange(e.target.value)}
              className="w-full mt-1 px-3 py-2 rounded-xl border border-zinc-200 focus:border-zinc-900 outline-none text-sm bg-white">
        {options.map((o) => (
          <option key={o.value} value={o.value}>{o.label}</option>
        ))}
      </select>
    </label>
  );
}

function Spinner({ text }) {
  return <PageSkeleton label={text} rows={4} />;
}
